import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  Plane, Home, UtensilsCrossed, Car, MapPin, Wallet, Camera, 
  CheckCircle, Globe, Trophy, Users, Clock, Send, ChevronDown,
  Instagram, Youtube, Link as LinkIcon
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AnimatedSection = ({ children, delay = 0 }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

const EliteResidency = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    nationality: '',
    current_location: '',
    email: '',
    phone: '',
    applying_as: '',
    primary_disciplines: '',
    years_experience: '',
    current_gym: '',
    competition_record: '',
    coaching_background: '',
    notable_achievements: '',
    preferred_duration: '',
    preferred_time_window: '',
    motivation: '',
    contribution: '',
    instagram: '',
    fight_footage: '',
    coaching_clips: '',
    website: '',
    declaration_accepted: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.declaration_accepted) {
      toast.error('Please accept the declaration to continue');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          page_slug: 'elite-residency',
          form_data: formData,
          utm_source: 'direct',
          utm_campaign: 'elite-residency',
        }),
      });

      if (res.ok) {
        // Redirect to thank you page for conversion tracking
        window.location.href = '/thank-you/elite-residency';
        return;
      } else {
        throw new Error('Submission failed');
      }
    } catch (err) {
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // What Cobra Thai Covers items
  const coverageItems = [
    { icon: Plane, text: 'Visa Support' },
    { icon: Plane, text: 'Return international or domestic flights' },
    { icon: Home, text: 'Accommodation' },
    { icon: UtensilsCrossed, text: 'Daily meals via in-house performance meal prep' },
    { icon: Car, text: 'Local transport for gym-related activity' },
    { icon: MapPin, text: 'Select sightseeing and excursions' },
    { icon: Wallet, text: 'Small stipend for personal spending' },
  ];

  // Past residents (placeholder)
  const pastResidents = [
    {
      name: 'Ashish Sethi',
      country: 'India',
      discipline: 'Muay Thai / Boxing',
      image: `${API_URL}/api/uploads/ashish-sethi.png`,
      quote: '3x Bangla Stadium Belt Champion\n2020 IFMA World Championship Bronze\nBest Fighter Award from legend Buakaw\nPtong Stadium Belt Holder\nMuay Thai National Championship\nKFI Kickboxing Championship',
    },
    {
      name: 'Westin Wilson',
      country: 'USA',
      discipline: 'MMA',
      image: `${API_URL}/api/uploads/westin-wilson.png`,
      quote: 'Pro MMA Fighter - UFC Roster\n17 Pro Wins (12 by Submission)\nUFC Win via Triangle Armbar (Round 1)\nTuff-N-Uff & Conflict MMA Champion',
    },
    {
      name: 'JB Chalbos',
      country: 'France',
      discipline: 'Brazilian Jiu-Jitsu',
      image: '/site-assets/jb-chalbos-coach.png',
      quote: 'AJP Thailand Champion\nIBJJF Dublin Champion\n3x Naga Europe Champion\n3x Irish Champion\nJapan Champion\n5x Grappling Industries Champion',
    },
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-cobra-black flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-cobra-dark border border-white/10 rounded-sm p-12 max-w-lg text-center"
        >
          <div className="w-20 h-20 bg-green-600/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="text-green-500" size={40} />
          </div>
          <h2 className="font-anton text-3xl text-white uppercase tracking-wider mb-4">
            Application Received
          </h2>
          <p className="text-white/70 font-manrope mb-6">
            Thank you for your interest in the Cobra Thai Elite Residency Programme. 
            We will review your application and contact shortlisted candidates directly.
          </p>
          <p className="text-white/50 text-sm font-manrope">
            Due to the selective nature of the programme, only shortlisted applicants will be contacted.
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="bg-cobra-black min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden" data-testid="residency-hero">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/6295832/pexels-photo-6295832.jpeg?auto=compress&cs=tinysrgb&w=1920)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-cobra-black/70 via-cobra-black/60 to-cobra-black" />
        
        <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            <span className="inline-block bg-cobra-red/20 border border-cobra-red/50 text-cobra-red px-4 py-2 rounded-sm text-sm font-bold uppercase tracking-wider mb-6">
              Selective Programme
            </span>
            <h1 className="font-anton text-5xl md:text-7xl lg:text-8xl text-white uppercase tracking-wider mb-6 leading-tight">
              COBRA THAI<br />
              <span className="text-cobra-red text-shadow-glow">ELITE RESIDENCY</span>
            </h1>
            <p className="font-manrope text-xl md:text-2xl text-white/80 mb-8 max-w-3xl mx-auto">
              Train. Coach. Live the Gym.
            </p>
            <motion.a
              href="#apply"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-3 bg-cobra-red hover:bg-red-700 text-white font-bold uppercase tracking-wider px-8 py-4 rounded-sm transition-all"
            >
              <Send size={20} />
              Apply Now
            </motion.a>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <ChevronDown className="text-white/50" size={32} />
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-20 px-4 md:px-8 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <AnimatedSection>
            <p className="font-manrope text-lg md:text-xl text-white/80 leading-relaxed mb-8">
              The Cobra Thai Elite Residency Programme is a <strong className="text-white">selective, short-term residency</strong> designed 
              for elite coaches and professional fighters across the world, and India.
            </p>
            <p className="font-manrope text-lg text-white/70 leading-relaxed mb-8">
              It is a <strong className="text-white">fully immersive experience</strong> inside Cobra Thai — training, coaching, 
              sharing knowledge, and living the day-to-day reality of a high-performance MMA gym.
            </p>
            <p className="font-manrope text-lg text-white/70 leading-relaxed">
              Each year, we open a <strong className="text-cobra-red">very limited number</strong> of residency slots for individuals 
              operating at a serious level who want meaningful exposure, development, and collaboration.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-20 px-4 md:px-8 lg:px-12 bg-cobra-dark/30">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <h2 className="font-anton text-4xl md:text-5xl text-white uppercase tracking-wider mb-12 text-center">
              WHO THIS IS <span className="text-cobra-red">FOR</span>
            </h2>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <AnimatedSection delay={0.1}>
              <div className="bg-cobra-black/50 border border-white/10 rounded-sm p-8 h-full hover:border-cobra-red/50 transition-colors">
                <Globe className="text-cobra-red mb-4" size={40} />
                <h3 className="font-anton text-xl text-white uppercase tracking-wide mb-3">
                  International Coaches
                </h3>
                <p className="font-manrope text-white/60">
                  MMA, Muay Thai, BJJ, Wrestling, Boxing coaches from around the world
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <div className="bg-cobra-black/50 border border-white/10 rounded-sm p-8 h-full hover:border-cobra-red/50 transition-colors">
                <Trophy className="text-cobra-red mb-4" size={40} />
                <h3 className="font-anton text-xl text-white uppercase tracking-wide mb-3">
                  Pro Fighters
                </h3>
                <p className="font-manrope text-white/60">
                  International professional or semi-professional fighters seeking development
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.3}>
              <div className="bg-cobra-black/50 border border-white/10 rounded-sm p-8 h-full hover:border-cobra-red/50 transition-colors">
                <Users className="text-cobra-red mb-4" size={40} />
                <h3 className="font-anton text-xl text-white uppercase tracking-wide mb-3">
                  Elite Indian Talent
                </h3>
                <p className="font-manrope text-white/60">
                  Coaches and pro fighters competing or coaching at national/international level
                </p>
              </div>
            </AnimatedSection>
          </div>

          <AnimatedSection delay={0.4}>
            <p className="font-manrope text-white/60 text-center mt-8 max-w-3xl mx-auto">
              While the programme has a strong international focus, we actively welcome India-based talent 
              who meet the same standards and want to be part of a globally connected training environment.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* What the Residency Involves */}
      <section className="py-20 px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <h2 className="font-anton text-4xl md:text-5xl text-white uppercase tracking-wider mb-4 text-center">
              WHAT THE RESIDENCY <span className="text-cobra-red">INVOLVES</span>
            </h2>
            <p className="font-manrope text-white/60 text-center mb-12 text-lg">
              Residents are fully integrated into Cobra Thai, not treated as visitors.
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <AnimatedSection delay={0.1}>
              <div className="bg-gradient-to-br from-cobra-red/10 to-transparent border border-cobra-red/30 rounded-sm p-8">
                <h3 className="font-anton text-2xl text-white uppercase tracking-wide mb-6">
                  Training & Coaching
                </h3>
                <ul className="space-y-4 font-manrope text-white/70">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="text-cobra-red flex-shrink-0 mt-1" size={18} />
                    <span>Morning and evening training blocks</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="text-cobra-red flex-shrink-0 mt-1" size={18} />
                    <span>Daily pro / fighters class</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="text-cobra-red flex-shrink-0 mt-1" size={18} />
                    <span>Coaching and co-coaching alongside our team</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="text-cobra-red flex-shrink-0 mt-1" size={18} />
                    <span>Technical exchange, drills, systems, and shared methodologies</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="text-cobra-red flex-shrink-0 mt-1" size={18} />
                    <span>Active involvement in the gym's training culture</span>
                  </li>
                </ul>
                <p className="font-manrope text-white/50 mt-6 text-sm italic">
                  This is a collaborative environment built on learning, contribution, and mutual respect.
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <div className="bg-cobra-black/50 border border-white/10 rounded-sm p-8">
                <h3 className="font-anton text-2xl text-white uppercase tracking-wide mb-6 flex items-center gap-3">
                  <Clock className="text-cobra-red" size={28} />
                  Residency Duration
                </h3>
                <p className="font-manrope text-white/70 mb-6">
                  Depending on profile and availability:
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-4 bg-white/5 rounded-sm">
                    <span className="font-bebas text-3xl text-cobra-red">2</span>
                    <div>
                      <span className="text-white font-bold">Weeks</span>
                      <p className="text-white/50 text-sm">Short, high-impact immersion</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-cobra-red/10 border border-cobra-red/30 rounded-sm">
                    <span className="font-bebas text-3xl text-cobra-red">4</span>
                    <div>
                      <span className="text-white font-bold">Weeks</span>
                      <p className="text-white/50 text-sm">Full integration (recommended)</p>
                    </div>
                  </div>
                </div>
                <p className="font-manrope text-white/50 mt-6 text-sm">
                  Final duration is confirmed on agreement.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* What Cobra Thai Covers */}
      <section className="py-20 px-4 md:px-8 lg:px-12 bg-gradient-to-b from-cobra-dark/50 to-cobra-black">
        <div className="max-w-5xl mx-auto">
          <AnimatedSection>
            <h2 className="font-anton text-4xl md:text-5xl text-white uppercase tracking-wider mb-4 text-center">
              WHAT COBRA THAI <span className="text-cobra-red">COVERS</span>
            </h2>
            <p className="font-manrope text-white/60 text-center mb-12 text-lg">
              This is a fully supported residency.
            </p>
          </AnimatedSection>

          <AnimatedSection delay={0.1}>
            <div className="bg-cobra-black border border-cobra-red/30 rounded-sm p-8 md:p-12">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {coverageItems.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-4"
                  >
                    <div className="w-10 h-10 bg-cobra-red/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="text-cobra-red" size={20} />
                    </div>
                    <span className="font-manrope text-white">{item.text}</span>
                  </motion.div>
                ))}
              </div>

              <div className="mt-8 pt-8 border-t border-white/10">
                <p className="font-manrope text-white/50 text-sm">
                  <strong className="text-white/70">Note:</strong> Residents are not paid a salary, and no employment relationship 
                  is created — this is a development-focused residency, not a contract role. Beyond personal discretionary spending, 
                  all core costs are covered.
                </p>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Content & Collaboration */}
      <section className="py-20 px-4 md:px-8 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <AnimatedSection>
            <div className="flex items-center gap-4 mb-8">
              <Camera className="text-cobra-red" size={40} />
              <h2 className="font-anton text-3xl md:text-4xl text-white uppercase tracking-wider">
                Content & Collaboration
              </h2>
            </div>
            <div className="space-y-4 font-manrope text-white/70">
              <p>During the residency:</p>
              <ul className="space-y-3 ml-4">
                <li className="flex items-start gap-3">
                  <span className="text-cobra-red">•</span>
                  Training and coaching sessions may be filmed or photographed
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-cobra-red">•</span>
                  Content may be used by Cobra Thai for marketing and documentation
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-cobra-red">•</span>
                  Collaborative content and mutual tagging are encouraged
                </li>
              </ul>
              <p className="text-white/50 italic mt-6">
                The aim is to authentically capture the experience — not to stage it.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Why Cobra Thai */}
      <section className="py-20 px-4 md:px-8 lg:px-12 bg-cobra-red/5">
        <div className="max-w-4xl mx-auto text-center">
          <AnimatedSection>
            <h2 className="font-anton text-4xl md:text-5xl text-white uppercase tracking-wider mb-8">
              WHY <span className="text-cobra-red">COBRA THAI</span>
            </h2>
            <p className="font-manrope text-lg text-white/80 mb-8">
              Cobra Thai is a <strong className="text-white">performance-driven MMA academy</strong>, not a commercial drop-in gym.
            </p>
            <p className="font-manrope text-white/70 mb-8">
              Residents train and coach alongside active competitors, professional fighters, 
              and coaches committed to continuous improvement.
            </p>
            <div className="bg-cobra-black/80 border border-white/10 rounded-sm p-8 max-w-2xl mx-auto">
              <p className="font-manrope text-white/60 text-lg">
                If you're looking for comfort or a holiday, <strong className="text-white">this isn't the programme</strong>.
              </p>
              <p className="font-manrope text-cobra-red text-lg mt-4 font-semibold">
                If you're looking for growth, challenge, and genuine exchange, you'll fit right in.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Meet Past Residents (Placeholder) */}
      <section className="py-20 px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <h2 className="font-anton text-4xl md:text-5xl text-white uppercase tracking-wider mb-4 text-center">
              MEET PAST <span className="text-cobra-red">RESIDENTS</span>
            </h2>
            <p className="font-manrope text-white/50 text-center mb-12">
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {pastResidents.map((resident, index) => (
              <AnimatedSection key={index} delay={index * 0.1}>
                <div className="bg-cobra-dark/50 border border-white/10 rounded-sm overflow-hidden group hover:border-cobra-red/50 transition-colors">
                  <div className="aspect-square overflow-hidden relative">
                    <img
                      src={resident.image}
                      alt={resident.name}
                      className="w-full h-full object-cover object-top opacity-50 group-hover:opacity-70 transition-opacity"
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-cobra-black via-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <span className="text-cobra-red text-sm font-bold uppercase">{resident.discipline}</span>
                      <h3 className="font-anton text-xl text-white">{resident.name}</h3>
                      <p className="text-white/60 text-sm">{resident.country}</p>
                    </div>
                  </div>
                  <div className="p-4">
                    {resident.quote.split('\n').map((line, i) => (
                      <p key={i} className="font-manrope text-white/50 text-sm">{line}</p>
                    ))}
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Selection Process */}
      <section className="py-20 px-4 md:px-8 lg:px-12 bg-cobra-dark/30">
        <div className="max-w-4xl mx-auto text-center">
          <AnimatedSection>
            <h2 className="font-anton text-4xl md:text-5xl text-white uppercase tracking-wider mb-8">
              SELECTION <span className="text-cobra-red">PROCESS</span>
            </h2>
            <p className="font-manrope text-lg text-white/80 mb-8">
              The Elite Residency Programme is <strong className="text-white">application-only</strong>.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {['Slots extremely limited', 'Manual review', 'Shortlist interviews', 'Experience & fit based'].map((item, i) => (
                <div key={i} className="bg-cobra-black/50 border border-white/10 rounded-sm p-4">
                  <p className="font-manrope text-white/70 text-sm">{item}</p>
                </div>
              ))}
            </div>
            <p className="font-manrope text-cobra-red font-semibold">
              There is no automatic acceptance.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Application Form */}
      <section id="apply" className="py-20 px-4 md:px-8 lg:px-12 scroll-mt-20">
        <div className="max-w-3xl mx-auto">
          <AnimatedSection>
            <h2 className="font-anton text-4xl md:text-5xl text-white uppercase tracking-wider mb-4 text-center">
              APPLY FOR THE <span className="text-cobra-red">PROGRAMME</span>
            </h2>
            <p className="font-manrope text-white/60 text-center mb-12">
              If you believe this programme aligns with your experience and goals, apply below.
              Shortlisted applicants will be contacted directly.
            </p>
          </AnimatedSection>

          <AnimatedSection delay={0.1}>
            <form onSubmit={handleSubmit} className="bg-cobra-dark/50 border border-white/10 rounded-sm p-8 md:p-12 space-y-8">
              
              {/* Personal Details */}
              <div>
                <h3 className="font-anton text-xl text-cobra-red uppercase tracking-wide mb-6">
                  Personal Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-white/60 mb-2">Full Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.full_name}
                      onChange={(e) => handleChange('full_name', e.target.value)}
                      className="w-full bg-cobra-black border border-white/20 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-cobra-red"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/60 mb-2">Nationality *</label>
                    <input
                      type="text"
                      required
                      value={formData.nationality}
                      onChange={(e) => handleChange('nationality', e.target.value)}
                      className="w-full bg-cobra-black border border-white/20 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-cobra-red"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/60 mb-2">Current Location *</label>
                    <input
                      type="text"
                      required
                      value={formData.current_location}
                      onChange={(e) => handleChange('current_location', e.target.value)}
                      className="w-full bg-cobra-black border border-white/20 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-cobra-red"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/60 mb-2">Email Address *</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className="w-full bg-cobra-black border border-white/20 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-cobra-red"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm text-white/60 mb-2">WhatsApp / Phone Number *</label>
                    <input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      className="w-full bg-cobra-black border border-white/20 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-cobra-red"
                    />
                  </div>
                </div>
              </div>

              {/* Profile */}
              <div>
                <h3 className="font-anton text-xl text-cobra-red uppercase tracking-wide mb-6">
                  Profile
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-white/60 mb-2">Applying as *</label>
                    <select
                      required
                      value={formData.applying_as}
                      onChange={(e) => handleChange('applying_as', e.target.value)}
                      className="w-full bg-cobra-black border border-white/20 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-cobra-red"
                    >
                      <option value="">Select...</option>
                      <option value="coach">Coach</option>
                      <option value="fighter">Fighter</option>
                      <option value="coach_and_fighter">Coach & Fighter</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-white/60 mb-2">Primary Discipline(s) *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g., MMA, Muay Thai, BJJ"
                      value={formData.primary_disciplines}
                      onChange={(e) => handleChange('primary_disciplines', e.target.value)}
                      className="w-full bg-cobra-black border border-white/20 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-cobra-red"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/60 mb-2">Years of Experience *</label>
                    <input
                      type="text"
                      required
                      placeholder="Coaching and/or competing"
                      value={formData.years_experience}
                      onChange={(e) => handleChange('years_experience', e.target.value)}
                      className="w-full bg-cobra-black border border-white/20 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-cobra-red"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/60 mb-2">Current Gym / Team Affiliation</label>
                    <input
                      type="text"
                      value={formData.current_gym}
                      onChange={(e) => handleChange('current_gym', e.target.value)}
                      className="w-full bg-cobra-black border border-white/20 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-cobra-red"
                    />
                  </div>
                </div>
              </div>

              {/* Background & Experience */}
              <div>
                <h3 className="font-anton text-xl text-cobra-red uppercase tracking-wide mb-6">
                  Background & Experience
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-white/60 mb-2">Competition Record (if applicable)</label>
                    <textarea
                      rows={3}
                      placeholder="Wins-Losses, notable fights, titles..."
                      value={formData.competition_record}
                      onChange={(e) => handleChange('competition_record', e.target.value)}
                      className="w-full bg-cobra-black border border-white/20 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-cobra-red resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/60 mb-2">Coaching Background or Certifications</label>
                    <textarea
                      rows={3}
                      value={formData.coaching_background}
                      onChange={(e) => handleChange('coaching_background', e.target.value)}
                      className="w-full bg-cobra-black border border-white/20 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-cobra-red resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/60 mb-2">Notable Achievements or Experience</label>
                    <textarea
                      rows={3}
                      value={formData.notable_achievements}
                      onChange={(e) => handleChange('notable_achievements', e.target.value)}
                      className="w-full bg-cobra-black border border-white/20 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-cobra-red resize-none"
                    />
                  </div>
                </div>
              </div>

              {/* Residency Preferences */}
              <div>
                <h3 className="font-anton text-xl text-cobra-red uppercase tracking-wide mb-6">
                  Residency Preferences
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-white/60 mb-2">Preferred Duration *</label>
                    <select
                      required
                      value={formData.preferred_duration}
                      onChange={(e) => handleChange('preferred_duration', e.target.value)}
                      className="w-full bg-cobra-black border border-white/20 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-cobra-red"
                    >
                      <option value="">Select...</option>
                      <option value="2_weeks">2 Weeks</option>
                      <option value="4_weeks">4 Weeks (Recommended)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-white/60 mb-2">Preferred Time Window</label>
                    <input
                      type="text"
                      placeholder="e.g., March 2025, Flexible"
                      value={formData.preferred_time_window}
                      onChange={(e) => handleChange('preferred_time_window', e.target.value)}
                      className="w-full bg-cobra-black border border-white/20 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-cobra-red"
                    />
                  </div>
                </div>
              </div>

              {/* Motivation */}
              <div>
                <h3 className="font-anton text-xl text-cobra-red uppercase tracking-wide mb-6">
                  Motivation
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-white/60 mb-2">
                      Why are you interested in the Cobra Thai Elite Residency Programme? *
                    </label>
                    <textarea
                      required
                      rows={4}
                      value={formData.motivation}
                      onChange={(e) => handleChange('motivation', e.target.value)}
                      className="w-full bg-cobra-black border border-white/20 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-cobra-red resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/60 mb-2">
                      What do you feel you can contribute during your stay? *
                    </label>
                    <textarea
                      required
                      rows={4}
                      value={formData.contribution}
                      onChange={(e) => handleChange('contribution', e.target.value)}
                      className="w-full bg-cobra-black border border-white/20 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-cobra-red resize-none"
                    />
                  </div>
                </div>
              </div>

              {/* Links */}
              <div>
                <h3 className="font-anton text-xl text-cobra-red uppercase tracking-wide mb-2">
                  Links
                </h3>
                <p className="text-white/40 text-sm mb-6">Optional but recommended</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-white/60 mb-2 flex items-center gap-2">
                      <Instagram size={16} /> Instagram / Social Media
                    </label>
                    <input
                      type="url"
                      placeholder="https://instagram.com/..."
                      value={formData.instagram}
                      onChange={(e) => handleChange('instagram', e.target.value)}
                      className="w-full bg-cobra-black border border-white/20 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-cobra-red"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/60 mb-2 flex items-center gap-2">
                      <Youtube size={16} /> Fight Footage or Highlights
                    </label>
                    <input
                      type="url"
                      placeholder="https://youtube.com/..."
                      value={formData.fight_footage}
                      onChange={(e) => handleChange('fight_footage', e.target.value)}
                      className="w-full bg-cobra-black border border-white/20 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-cobra-red"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/60 mb-2 flex items-center gap-2">
                      <Youtube size={16} /> Coaching or Seminar Clips
                    </label>
                    <input
                      type="url"
                      placeholder="https://..."
                      value={formData.coaching_clips}
                      onChange={(e) => handleChange('coaching_clips', e.target.value)}
                      className="w-full bg-cobra-black border border-white/20 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-cobra-red"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-white/60 mb-2 flex items-center gap-2">
                      <LinkIcon size={16} /> Website
                    </label>
                    <input
                      type="url"
                      placeholder="https://..."
                      value={formData.website}
                      onChange={(e) => handleChange('website', e.target.value)}
                      className="w-full bg-cobra-black border border-white/20 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-cobra-red"
                    />
                  </div>
                </div>
              </div>

              {/* Declaration */}
              <div className="pt-6 border-t border-white/10">
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={formData.declaration_accepted}
                    onChange={(e) => handleChange('declaration_accepted', e.target.checked)}
                    className="mt-1 w-5 h-5 rounded border-white/20 bg-cobra-black text-cobra-red focus:ring-cobra-red"
                  />
                  <span className="text-white/70 text-sm group-hover:text-white transition-colors">
                    I understand this is a selective programme and that Cobra Thai reserves the right to accept or decline applications.
                  </span>
                </label>
              </div>

              {/* Submit */}
              <motion.button
                type="submit"
                disabled={submitting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full bg-cobra-red hover:bg-red-700 text-white font-bold uppercase tracking-wider py-4 rounded-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
              >
                {submitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    Submit Application
                  </>
                )}
              </motion.button>

              <p className="text-white/40 text-sm text-center">
                Due to the selective nature of the programme, only shortlisted applicants will be contacted.
              </p>
            </form>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default EliteResidency;
